import { useRef, useEffect, useMemo } from 'react';
import { CONTENT_TYPES, CONTENT_TYPE_COLORS } from '@/lib/constants';
import { ContentType } from '@/types/custom';
import { PlayStats } from '@/lib/hooks/use-content-plays';

interface DonutChartProps {
  contentPlays: PlayStats;
}

export function DonutChart({ contentPlays }: DonutChartProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const appUsageData = useMemo(() => {
    const total = Object.values(contentPlays.types).reduce(
      (sum, value) => sum + (value?.dur_sec || 0),
      0,
    );

    if (total === 0) {
      return Object.entries(CONTENT_TYPES).map(([type, name]) => ({
        name,
        value: 0,
        color: CONTENT_TYPE_COLORS[type as ContentType],
      }));
    }

    return Object.entries(contentPlays.types)
      .filter(([, stat]) => stat?.dur_sec && stat?.dur_sec > 0)
      .map(([type, stat]) => ({
        name: CONTENT_TYPES[type as ContentType],
        value: Math.round(((stat?.dur_sec || 0) / total) * 100),
        color: CONTENT_TYPE_COLORS[type as ContentType],
      }))
      .sort((a, b) => b.value - a.value);
  }, [contentPlays]);

  // Calculate total time (in hours) - estimate based on content plays
  // Assuming average session length of 10 minutes
  const totalHours = useMemo(() => {
    const totalPlays = Object.values(contentPlays.types).reduce(
      (sum, value) => sum + (value?.dur_sec || 0),
      0,
    );
    return totalPlays / 60 / 60;
  }, [contentPlays]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;

    // Set canvas dimensions accounting for device pixel ratio
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    // Clear canvas
    ctx.clearRect(0, 0, rect.width, rect.height);

    // Calculate center and radius
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const outerRadius = Math.min(centerX, centerY) * 0.8;
    const innerRadius = outerRadius * 0.6; // Create donut hole

    // Draw donut segments
    let startAngle = 0;

    appUsageData.forEach((segment) => {
      if (segment.value === 0) return; // Skip segments with 0 value

      const segmentAngle = (segment.value / 100) * Math.PI * 2;

      // Draw segment
      ctx.beginPath();
      ctx.arc(
        centerX,
        centerY,
        outerRadius,
        startAngle,
        startAngle + segmentAngle,
      );
      ctx.arc(
        centerX,
        centerY,
        innerRadius,
        startAngle + segmentAngle,
        startAngle,
        true,
      );
      ctx.closePath();

      ctx.fillStyle = segment.color;
      ctx.fill();

      // Draw percentage label
      const labelAngle = startAngle + segmentAngle / 2;
      const labelX =
        centerX + (Math.cos(labelAngle) * (outerRadius + innerRadius)) / 2;
      const labelY =
        centerY + (Math.sin(labelAngle) * (outerRadius + innerRadius)) / 2;

      ctx.font = 'bold 14px Arial';
      ctx.fillStyle = '#FFFFFF';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(`${segment.value}%`, labelX, labelY);

      startAngle += segmentAngle;
    });

    // Draw center text
    ctx.fillStyle = '#000000';
    ctx.font = 'bold 24px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // Format hours and minutes
    const hours = Math.floor(totalHours);
    const minutes = Math.round((totalHours - hours) * 60);
    ctx.fillText(`${hours}u ${minutes}m`, centerX, centerY);
  }, [appUsageData, totalHours]);

  return (
    <div className="relative w-full h-full flex flex-col items-center">
      <canvas ref={canvasRef} className="w-full h-[220px]" />
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mt-4 w-full px-4">
        {appUsageData.map(
          (segment, index) =>
            segment.value > 0 && (
              <div key={index} className="flex items-center gap-1">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: segment.color }}
                />
                <span className="truncate">{segment.name}</span>
              </div>
            ),
        )}
      </div>
    </div>
  );
}
